/**
 * PATH: packages/email-hub/src/feature/components/integrationsOAuthListener.ts
 * FILE_ID: EMAILHUB-INTEGRATIONS-OAUTH-LISTENER-001
 * PURPOSE: Side-effect module that listens for OAuth callback redirects so
 *          the shell IntegrationsBanner refreshes immediately after the
 *          user finishes a Xero / Outlook / Gmail flow in another tab.
 * WHY:     Lifted verbatim from the (formerly duplicated) bottom of
 *          apps/{sam,newmark}/src/shell/IntegrationsBanner.tsx so the
 *          callback handling lives once. Importing IntegrationsBanner.tsx
 *          activates this listener (it `import`s this module for effect).
 *
 * Both flows append `?xero_connected=true` / `?outlook_connected=true` /
 * `?gmail_connected=true` to the landing URL. SAM never produces the gmail
 * param (Outlook-only), so the gmail branch is a harmless no-op there.
 */

import { XERO_STATUS_REFRESH_EVENT } from '@quotech/tenant-shared/integrations/useXeroStatus'
import { notify } from '@quotech/tenant-shared/stores/notificationStore'

function stripParam(name: string): void {
  try {
    const url = new URL(window.location.href)
    url.searchParams.delete(name)
    window.history.replaceState({}, '', url.toString())
  } catch {
    /* ignore */
  }
}

function checkUrlForConnect(): void {
  const params = new URLSearchParams(window.location.search)
  const xeroConnected = params.get('xero_connected') === 'true'
  const outlookConnected = params.get('outlook_connected') === 'true'
  const gmailConnected = params.get('gmail_connected') === 'true'
  if (xeroConnected || outlookConnected || gmailConnected) {
    window.dispatchEvent(new Event(XERO_STATUS_REFRESH_EVENT))
  }
  // 2026-07-08 "took time for it to reflect": adding an EXTRA mailbox
  // doesn't change the connected/expired booleans, so the status pollers
  // never fired their change events and the mailbox switcher waited out
  // its 60s poll. These no-detail events make useEmailAccounts reload
  // immediately on the OAuth landing (the status hooks ignore events
  // without a detail payload, so they're unaffected).
  if (outlookConnected) {
    window.dispatchEvent(new CustomEvent('outlook:status-changed'))
  }
  if (gmailConnected) {
    window.dispatchEvent(new CustomEvent('gmail:status-changed'))
  }
  if (xeroConnected) {
    notify({
      title: 'Xero connected',
      body: 'Authorisation completed via redirect - your tenants are now linked.',
      kind: 'success',
      source: 'xero',
      cta: { label: 'Open Integrations', href: '/settings?tab=integrations' },
    })
    stripParam('xero_connected')
  }
  if (outlookConnected) {
    notify({
      title: 'Outlook connected',
      body: 'Mailbox authorised - emails will start syncing within a minute.',
      kind: 'success',
      source: 'outlook',
      cta: { label: 'Open Email', href: '/email' },
    })
    stripParam('outlook_connected')
  }
  if (gmailConnected) {
    notify({
      title: 'Gmail connected',
      body: 'Mailbox authorised - emails will start syncing within a minute.',
      kind: 'success',
      source: 'gmail',
      cta: { label: 'Open Email', href: '/email' },
    })
    stripParam('gmail_connected')
  }
}

if (typeof window !== 'undefined') {
  // Idempotency guard: a tenant could (in theory) import the banner from
  // more than one entry during HMR; install the listeners exactly once.
  const w = window as Window & { __quotechIntegrationsOAuthListenerInstalled?: boolean }
  if (!w.__quotechIntegrationsOAuthListenerInstalled) {
    w.__quotechIntegrationsOAuthListenerInstalled = true
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        window.dispatchEvent(new Event(XERO_STATUS_REFRESH_EVENT))
      }
    })
    checkUrlForConnect()
  }
}
